import { db } from './firebase';
import { collection, onSnapshot, doc, setDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import type { Mission, EnlistedDad, ContributionRole, KidPolicy } from '../types';
import { awardBadge, checkAndAutoAwardBadges } from './badgeService';
import { getCurrentProfile } from './authService';

type MissionsListener = (missions: Mission[]) => void;
const listeners: MissionsListener[] = [];

let missionsCache: Mission[] = [];

export function subscribeMissions(listener: MissionsListener): () => void {
  listeners.push(listener);
  listener(missionsCache);

  const colRef = collection(db, 'missions');
  const unsub = onSnapshot(colRef, (snapshot) => {
    const items: Mission[] = [];
    snapshot.forEach(d => items.push({ id: d.id, ...d.data() } as Mission));
    missionsCache = items.sort((a, b) => b.createdAt - a.createdAt);
    listeners.forEach(fn => fn(missionsCache));
  }, (err) => {
    console.warn('[Pops Culture] Firestore missions snapshot warning:', err);
  });

  return () => {
    unsub();
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

function notify() {
  listeners.forEach(fn => fn(missionsCache));
}

export async function createMission(
  newMission: Omit<Mission, 'id' | 'createdAt' | 'enlisted'>, 
  hostWaiverConfirmed: boolean
): Promise<Mission> {
  if (!hostWaiverConfirmed) {
    throw new Error('Host must confirm property authority, permits, and job site safety.');
  }

  const mission: Mission = {
    ...newMission,
    id: `evt_${Date.now()}`,
    createdAt: Date.now(),
    enlisted: [
      {
        userId: newMission.host.userId,
        name: newMission.host.name,
        avatarInitials: newMission.host.avatarInitials,
        role: 'Event Host',
        roleType: 'host',
        toolPledged: 'Job Site & Refreshments',
        enlistedAt: Date.now()
      }
    ]
  };

  missionsCache.unshift(mission);
  notify();

  try {
    await setDoc(doc(db, 'missions', mission.id), mission);
    const profile = getCurrentProfile();
    if (profile && profile.uid === newMission.host.userId) {
      await checkAndAutoAwardBadges(profile, missionsCache, []);
    }
  } catch (e) {
    console.warn('[Pops Culture] Failed to save mission to Firestore:', e);
  }

  return mission;
}

export async function enlistInMission(
  missionId: string, 
  user: { uid: string; name: string; avatarInitials: string }, 
  role: string, 
  roleType: ContributionRole, 
  toolPledged: string,
  assumptionOfRiskConfirmed: boolean
): Promise<void> {
  if (!assumptionOfRiskConfirmed) {
    throw new Error('You must accept the Assumption of Risk & Liability Waiver before enlisting.');
  }

  const mission = missionsCache.find(m => m.id === missionId);
  if (!mission) throw new Error('Mission not found');

  if (mission.enlisted.some(e => e.userId === user.uid)) {
    throw new Error('Already enlisted in this mission');
  }

  if (mission.enlisted.length >= mission.maxSlots) {
    throw new Error('This mission has reached maximum capacity.');
  }

  const newEntry: EnlistedDad = {
    userId: user.uid,
    name: user.name,
    avatarInitials: user.avatarInitials,
    role,
    roleType,
    toolPledged: toolPledged.trim() || 'Pair of hands & work gloves',
    enlistedAt: Date.now()
  };

  const isFirstAttendee = mission.enlisted.length === 1;

  mission.enlisted.push(newEntry);
  notify();

  try {
    const ref = doc(db, 'missions', missionId);
    await updateDoc(ref, {
      enlisted: arrayUnion(newEntry)
    });

    // Award "First on the Scene" if this user was the first dad to RSVP across any event
    if (isFirstAttendee) {
      await awardBadge(user.uid, 'b1');
    }

    const profile = getCurrentProfile();
    if (profile && profile.uid === user.uid) {
      await checkAndAutoAwardBadges(profile, missionsCache, []);
    }
  } catch (e) {
    console.warn('[Pops Culture] Failed to update enlistment in Firestore:', e);
  }
}

export async function withdrawFromMission(missionId: string, userId: string): Promise<void> {
  const mission = missionsCache.find(m => m.id === missionId);
  if (!mission) return;

  const target = mission.enlisted.find(e => e.userId === userId);
  if (!target) return;

  mission.enlisted = mission.enlisted.filter(e => e.userId !== userId);
  notify();

  try {
    const ref = doc(db, 'missions', missionId);
    await updateDoc(ref, {
      enlisted: arrayRemove(target)
    });
  } catch (e) {
    console.warn('[Pops Culture] Failed to remove enlistment in Firestore:', e);
  }
}

import { getZipDistance } from '../utils/zipUtils';

export interface MissionFilterParams {
  type: string;
  kidPolicy: string;
  startDate?: string;
  endDate?: string;
  search: string;
  zipCode?: string;
  radiusMiles?: number;
  viewerUid?: string;
  isAdmin?: boolean;
}

export function filterMissions(
  missions: Mission[], 
  filters: MissionFilterParams
): Mission[] {
  const radius = filters.radiusMiles ?? 50;
  const userZip = filters.zipCode?.trim();

  return missions.filter(evt => {
    // 0. Moderation status: unlist under_review events for general public
    if (evt.status === 'removed') return false;
    if (evt.status === 'under_review') {
      const isCreator = filters.viewerUid && evt.host.userId === filters.viewerUid;
      if (!filters.isAdmin && !isCreator) {
        return false; // Unlisted for everyone else
      }
    }

    // 1. Mission Type filter
    if (filters.type && filters.type !== 'all' && evt.type !== filters.type) return false;

    // 2. Kid Policy filter
    if (filters.kidPolicy && filters.kidPolicy !== 'all' && evt.kidPolicy !== filters.kidPolicy) return false;

    // 3. Free text search across title AND description
    if (filters.search && filters.search.trim()) {
      const q = filters.search.toLowerCase().trim();
      const matchTitle = evt.title.toLowerCase().includes(q);
      const matchDesc = evt.description.toLowerCase().includes(q);
      if (!matchTitle && !matchDesc) return false;
    }

    // 4. Date range filter
    if (filters.startDate || filters.endDate) {
      // Extract ISO date from event datetime string or tentative dates or createdAt
      const dateMatch = evt.datetime.match(/\d{4}-\d{2}-\d{2}/) || evt.datetime.match(/[A-Za-z]+ \d{1,2}(?:, \d{4})?/);
      let eventDateIso = '';
      if (dateMatch) {
        const parsed = Date.parse(dateMatch[0]);
        if (!isNaN(parsed)) {
          eventDateIso = new Date(parsed).toISOString().split('T')[0];
        }
      }
      if (!eventDateIso && evt.createdAt) {
        eventDateIso = new Date(evt.createdAt).toISOString().split('T')[0];
      }

      if (eventDateIso) {
        if (filters.startDate && eventDateIso < filters.startDate) return false;
        if (filters.endDate && eventDateIso > filters.endDate) return false;
      }
    }

    // 5. ZIP Code & Distance filter (default within 50 miles)
    if (userZip && /^\d{5}$/.test(userZip) && evt.zipCode) {
      const dist = getZipDistance(userZip, evt.zipCode);
      if (dist !== null && dist > radius) {
        return false;
      }
    }

    return true;
  });
}
